import http from "@services/httpService"

/**
 * Dashboard Sales Admin (Feature-0199). Backend: `SalesDashboardAppService`
 * (`api/services/app/SalesDashboard/*`). Mọi method là read-only.
 */

/** Widget W-SAL-01 — một bước chuyển đổi của funnel. */
export interface IFunnelStepRate {
  /** Khoá i18n của bậc trước. */
  fromStep: string
  toStep: string
  /** `null` khi bậc trên bằng 0 — KHÁC 0%. */
  rate?: number | null
}

/**
 * Widget W-SAL-01 — funnel theo KỲ (không phải cohort).
 * `opportunityCount` LUÔN null ở hiện trạng: entity Opportunity không được map vào DB (TD-34).
 */
export interface IFunnel {
  leadCount: number
  opportunityCount?: number | null
  reservedCount: number
  depositedCount: number
  contractCount: number
  depositCancelRate?: number | null
  conversionRates: IFunnelStepRate[]
  /** `true` ⇒ bậc Lead vẫn là số TOÀN HỆ vì bảng khách hàng không có cột dự án. */
  leadStagesIgnoreProjectFilter: boolean
}

/** Chiều gộp của funnel theo kênh. 1 nguồn khách · 2 sàn · 3 NVKD. */
export type FunnelChannelGroupBy = 1 | 2 | 3

/** Widget W-SAL-02 — một kênh. */
export interface IChannelFunnel {
  /** `null` = chưa gán nguồn/sàn/NVKD. */
  channelId?: number | null
  channelName?: string
  /** `null` khi chiều đang gộp không áp dụng cho bậc Lead (sàn/NVKD). */
  leadCount?: number | null
  reservedCount: number
  depositedCount: number
  contractCount: number
  leadToContractRate?: number | null
}

/**
 * Widget W-SAL-05 — một dòng của endpoint CŨ `Dashboard/GetSummaryUnitStatusWithProject`.
 * Khai báo ở đây (không phải `any`) để widget và store cùng một hợp đồng dữ liệu, dù endpoint là của
 * dashboard cũ và service của nó chưa có type.
 */
export interface ILegacyUnitStatusRow {
  projectId?: number | null
  project?: { projectName?: string }
  summaryDetail?: {
    totalUnit?: number
    unitAvailable?: number
    unitBooked?: number
    unitDeposited?: number
    unitSold?: number
  }
}

/** Widget W-SAL-06 — một dòng của endpoint CŨ `Dashboard/GetSummaryAgencyMasterPerformance`. */
export interface ILegacyAgencyPerformanceRow {
  agencyMaster?: { id?: number; name?: string }
  value?: number
}

/** Widget W-SAL-07 — một bảng giá đang chờ duyệt. */
export interface IPendingPricingItem {
  pricingApprovalId: number
  name?: string
  projectId: number
  projectName?: string
  /** Thời điểm GỬI DUYỆT (theo phiếu), không phải thời điểm tạo bảng giá. */
  requestedAt: string
  ageDays: number
}

/**
 * Widget W-SAL-07 — bảng giá đang chờ duyệt.
 * "Đang chờ" dùng cùng predicate với hàng đợi phê duyệt W-SA-03, nên bảng giá đã qua bước 1 vẫn nằm đây.
 */
export interface IPendingPricing {
  pendingCount: number
  /** `null` khi không có phiếu nào — KHÁC "0 ngày". */
  oldestPendingDays?: number | null
  items: IPendingPricingItem[]
}

/** Widget W-SAL-04 — một điểm của biểu đồ booking theo thời gian. */
export interface IBookingTrendPoint {
  /** Nhãn mốc do báo cáo gốc sinh (ngày / tuần / tháng). */
  bucket: string
  totalBookings: number
}

/**
 * Widget W-SAL-04 — booking theo thời gian, lấy nguyên từ báo cáo booking.
 * ⚠️ Con số là "booking CHỜ ĐẾN LƯỢT" (báo cáo gốc chỉ đếm trạng thái PENDING), không phải tổng booking
 * phát sinh trong kỳ (TD-35).
 */
export interface IBookingTrend {
  points: IBookingTrendPoint[]
  /** `true` = đang lọc dự án nhưng báo cáo gốc không lọc theo dự án. */
  ignoresProjectFilter: boolean
  /** `true` = thiếu mốc kỳ nên báo cáo gốc không trả dữ liệu. */
  requiresPeriod: boolean
}

/**
 * Widget W-SAL-03 — hiệu quả một sale event.
 * Mẫu số của mọi tỷ lệ là TOÀN BỘ thư mời của sự kiện (khác màn sự kiện — màn đó chỉ đếm thư mời của
 * booking đã được duyệt chuyển hợp đồng).
 */
export interface ISaleEventPerf {
  salesEventId: number
  salesEventCode?: string
  salesEventName?: string
  startDate: string
  invitationCount: number
  checkinCount: number
  /** `null` khi sự kiện chưa có thư mời nào — KHÁC 0%. */
  checkinRate?: number | null
  ballotingCount: number
  closedAtEventCount: number
  closedAtEventRate?: number | null
  eventBookingValue: number
}

/** Widget W-SA-04 — mức thiếu của hồ sơ thu. 1 = chưa cấu hình lịch · 2 = có lịch, chưa phát hành nợ. */
export type ScheduleSetupMissingLevel = 1 | 2

/** Widget W-SA-04 — một hợp đồng còn thiếu bước setup thu. */
export interface IScheduleSetupItem {
  contractId: number
  contractCode?: string
  projectId: number
  projectName?: string
  validDate: string
  missingLevel: ScheduleSetupMissingLevel
}

/**
 * Widget W-SA-04 — hợp đồng đủ điều kiện thu mà chưa xong bước setup.
 * ⚠️ Phần "hợp đồng thu một lần chỉ áp mức (b)" của spec KHÔNG thi hành được — `ContractPaymentTypeEnum`
 * là code chết và `Contract` không có cột kiểu thanh toán (TD-33).
 */
export interface IScheduleSetup {
  noScheduleCount: number
  scheduleNotIssuedCount: number
  /** Chặn thu — đếm riêng để đối soát, KHÔNG phải hồ sơ làm dở. */
  blockedPaymentCount: number
  items: IScheduleSetupItem[]
}

/** Widget W-SA-02 — một trạng thái hợp đồng trong pipeline. */
export interface IContractStatusBucket {
  /** Giá trị `ContractStatusEnum`: 1 Draft · 2 Waitting · 3 Completed · 4 Cancel · 5 Booking. */
  status: number
  count: number
}

/** Widget W-SA-02 — một giai đoạn hợp đồng trong pipeline. */
export interface IContractPhaseBucket {
  /** `null` = hợp đồng chưa gắn giai đoạn. */
  phase?: number | null
  /** Nhãn do backend dịch từ bộ nhãn đang có của module hợp đồng. */
  phaseLabel?: string
  count: number
}

/**
 * Widget W-SA-02 — pipeline hợp đồng.
 * "Chờ ký" = hợp đồng ĐÃ DUYỆT XONG mà chưa có Ngày văn bản thỏa thuận (PO chốt 31/07) ⇒ tập này RỜI
 * HẲN với hàng đợi phê duyệt W-SA-03.
 */
export interface IContractPipeline {
  byStatus: IContractStatusBucket[]
  byPhase: IContractPhaseBucket[]
  awaitingSignCount: number
  /** Tập con của `awaitingSignCount`, đo từ mốc DUYỆT XONG. */
  awaitingSignOverdueCount: number
  warningDays: number
  /** `null` = kỳ không có hợp đồng nào đủ điều kiện tính, KHÁC "0 ngày". */
  avgBookingToContractDays?: number | null
  pendingTerminationCount: number
  terminatedInPeriodCount: number
}

/** Widget W-SA-03 — một loại phiếu trong hàng đợi phê duyệt. */
export interface IApprovalQueueType {
  /** Giá trị enum `RequestTypeEnum`. */
  requestType: number
  /** Nhãn loại phiếu do backend dịch (nguồn dùng chung với màn cấu hình phê duyệt). */
  typeLabel?: string
  count: number
  /** Phần của `count` đã chờ quá ngưỡng cảnh báo. */
  overWarningCount: number
}

/** Widget W-SA-03 — một bước duyệt đang có phiếu tồn. */
export interface IApprovalQueueStep {
  step: number
  count: number
  /** Số ngày TRUNG BÌNH các phiếu đang tồn đã nằm ở bước này. */
  avgDwellDays: number
}

/** Widget W-SA-03 — một phiếu trong danh sách chờ lâu nhất. */
export interface IApprovalQueueItem {
  requestId: number
  parentCode?: string
  displayName?: string
  requestType: number
  typeLabel?: string
  ageDays: number
  /** Thời gian phiếu đã nằm ở bước hiện tại — "người đang giữ giữ bao lâu rồi". */
  currentStepDwellDays: number
  currentStep: number
  /**
   * Người đang phải xử lý. RỖNG là trạng thái hợp lệ và đáng chú ý: bước hiện tại không còn người
   * duyệt hợp lệ (nhóm bị tắt / người đã nghỉ) hoặc cấu hình luồng đã bị xoá.
   */
  waitingOnUserNames: string[]
}

/**
 * Widget W-SA-03 — hàng đợi phê duyệt.
 * KHÔNG lọc theo kỳ: đây là tồn đọng hiện tại, giống màn Inbox. Chỉ phạm vi dự án được áp.
 */
export interface IApprovalQueue {
  totalPending: number
  overWarningCount: number
  /** Ngưỡng "chờ quá lâu" (ngày) đang cấu hình — nhãn cảnh báo đọc từ đây, không hardcode. */
  warningDays: number
  byType: IApprovalQueueType[]
  byStep: IApprovalQueueStep[]
  topOldest: IApprovalQueueItem[]
}

/** Widget W-SA-01 — một dự án trong phân bố booking sắp hết hạn. */
export interface IExpiringBookingProject {
  /** `null` = đợt chưa gắn dự án. */
  projectId?: number | null
  projectName?: string
  expiringCount: number
  overdueUnprocessedCount: number
}

/** Widget W-SA-01 — một booking cần xử lý. */
export interface IExpiringBookingItem {
  bookingId: number
  bookingCode?: string
  /** Đợt của booking — deep-link về danh sách booking lọc theo đợt. */
  cartId: number
  projectId?: number | null
  customerName?: string
  /** Rỗng khi booking chưa chọn căn (trạng thái PENDING). */
  unitCode?: string
  expiredDate: string
  /** Làm tròn xuống: 0 = hết hạn trong 24 giờ tới, ÂM = đã quá hạn. */
  daysLeft: number
}

/**
 * Widget W-SA-01 — booking sắp hết hạn giữ chỗ + tồn đọng quá hạn.
 * Hạn giữ chỗ là hạn theo ĐỢT (`Booking.ExpiredDate` = `Cart.EndBooking`), không phải theo từng booking.
 */
export interface IExpiringBookings {
  expiringCount: number
  /** Tập con của `expiringCount` chưa cọc DA. */
  expiringNoDaCount: number
  /** Đã quá hạn mà chưa ai xử lý — hệ thống không tự chuyển trạng thái nên đây là tồn đọng thật. */
  overdueUnprocessedCount: number
  warningDays: number
  byProject: IExpiringBookingProject[]
  /** 10 dòng cần xử lý sớm nhất, gồm cả nhóm quá hạn (`daysLeft` âm). */
  items: IExpiringBookingItem[]
}

class DashboardSalesService {
  public async getExpiringBookings(
    params?: Record<string, unknown>
  ): Promise<IExpiringBookings> {
    const res = await http.get(
      "api/services/app/SalesDashboard/GetExpiringBookings",
      { params }
    )
    return res.data.result
  }

  public async getPendingPricingApproval(
    params?: Record<string, unknown>
  ): Promise<IPendingPricing> {
    const res = await http.get(
      "api/services/app/SalesDashboard/GetPendingPricingApproval",
      { params }
    )
    return res.data.result
  }

  public async getBookingTrend(
    params?: Record<string, unknown>
  ): Promise<IBookingTrend> {
    const res = await http.get(
      "api/services/app/SalesDashboard/GetBookingTrend",
      { params }
    )
    return res.data.result
  }

  public async getSaleEventPerformance(
    params?: Record<string, unknown>
  ): Promise<ISaleEventPerf[]> {
    const res = await http.get(
      "api/services/app/SalesDashboard/GetSaleEventPerformance",
      { params }
    )
    return res.data.result
  }

  public async getFunnel(
    params?: Record<string, unknown>
  ): Promise<IFunnel> {
    const res = await http.get("api/services/app/SalesDashboard/GetFunnel", {
      params,
    })
    return res.data.result
  }

  public async getFunnelByChannel(
    params?: Record<string, unknown>
  ): Promise<IChannelFunnel[]> {
    const res = await http.get(
      "api/services/app/SalesDashboard/GetFunnelByChannel",
      { params }
    )
    return res.data.result
  }

  public async getPaymentScheduleSetup(
    params?: Record<string, unknown>
  ): Promise<IScheduleSetup> {
    const res = await http.get(
      "api/services/app/SalesDashboard/GetPaymentScheduleSetup",
      { params }
    )
    return res.data.result
  }

  public async getContractPipeline(
    params?: Record<string, unknown>
  ): Promise<IContractPipeline> {
    const res = await http.get(
      "api/services/app/SalesDashboard/GetContractPipeline",
      { params }
    )
    return res.data.result
  }

  public async getApprovalQueue(
    params?: Record<string, unknown>
  ): Promise<IApprovalQueue> {
    const res = await http.get(
      "api/services/app/SalesDashboard/GetApprovalQueue",
      { params }
    )
    return res.data.result
  }
}

export default new DashboardSalesService()
